import { getProductConfig } from '@/app/config/products.config';
import { commonQuotesService } from '../quotes/common';
import { businessProposalsService, BusinessProposalsService } from './business';
import {
  capitalGlobalProposalsService,
  CapitalGlobalProposalsService,
} from './capital-global';
import { commonProposalsService } from './common';
import {
  homeownerProposalsService,
  HomeownerProposalsService,
} from './homeowner';
import type {
  AttachPaymentInfoResponse,
  AttachQuotePayload,
  CalculateProposalResponse,
  GeneratePdfPayload,
  GeneratePdfResponse,
  GetProposalDocumentResponse,
  GetProposalsParams,
  InitiateProposalPayload,
  InitiateProposalResponse,
  PaymentInfoPayload,
  Proposal,
  ProposalCategories,
  ProposalsResponse,
  Schema,
  SendProposalEmailPayload,
} from './types';

export * from './business';
export * from './capital-global';
export * from './common';
export * from './group-life';
export * from './homeowner';
export type * from './types';

function pickProductService(
  productCode: string | undefined | null
):
  | HomeownerProposalsService
  | BusinessProposalsService
  | CapitalGlobalProposalsService {
  const proposalService = productCode
    ? getProductConfig(productCode)?.proposalService
    : undefined;

  switch (proposalService) {
    case 'business':
      return businessProposalsService;
    case 'capital-global':
      return capitalGlobalProposalsService;
    case 'homeowner':
    default:
      return homeownerProposalsService;
  }
}

/**
 * Fachada do módulo de propostas. Encapsula a divisão por produto
 * (residencial / empresarial) feita no backend e expõe uma API única
 * para os callers do frontend.
 *
 * Para chamadas que já conhecem o produto (todo o fluxo após o GET
 * inicial), passe `productCode` para rotear diretamente à controller
 * correta. A leitura por policyId usa a rota agnóstica de cotações
 * (`/portal/v1/quotes/{id}`), que retorna o mesmo payload independente
 * do produto.
 */
export class ProposalsService {
  async initiateProposal(payload: InitiateProposalPayload): Promise<{
    success: boolean;
    data?: InitiateProposalResponse;
    error?: string;
    message?: string;
  }> {
    return commonProposalsService.initiateProposal(payload);
  }

  async cancelProposal(policyId: string): Promise<{
    success: boolean;
    data?: { policyId: string; statusPortal: string };
    error?: string;
  }> {
    return commonProposalsService.cancelProposal(policyId);
  }

  async getProposals(params: GetProposalsParams = {}): Promise<{
    success: boolean;
    data?: ProposalsResponse;
    error?: string;
  }> {
    return commonProposalsService.getProposals(params);
  }

  /**
   * Busca proposta por policyId via rota agnóstica de cotações. O backend
   * resolve o produto a partir do próprio policyId, então não é preciso
   * chamar a controller específica para apenas ler o registro.
   */
  async getProposal(proposalId: string): Promise<{
    success: boolean;
    data?: Proposal;
    error?: string;
  }> {
    const response = await commonQuotesService.getProposalDetails(proposalId);
    return {
      success: response.success,
      data: response.data as unknown as Proposal | undefined,
      error: response.error,
    };
  }

  async attachPaymentInfo(
    policyId: string,
    payload: PaymentInfoPayload,
    productCode?: string | null
  ): Promise<AttachPaymentInfoResponse> {
    return pickProductService(productCode).attachPaymentInfo(policyId, payload);
  }

  async updateProposal(
    proposalId: string,
    payload: Partial<AttachQuotePayload>,
    productCode?: string | null
  ): Promise<{
    success: boolean;
    data?: Proposal;
    error?: string;
  }> {
    return pickProductService(productCode).updateProposal(proposalId, payload);
  }

  async getSchema(category: ProposalCategories): Promise<{
    success: boolean;
    data?: Schema;
    error?: string;
  }> {
    return commonProposalsService.getSchema(category);
  }

  async calculateProposal(
    policyId: string,
    productCode?: string | null
  ): Promise<CalculateProposalResponse> {
    return pickProductService(productCode).calculateProposal(policyId);
  }

  async generatePdf(
    proposalId: string,
    payload: GeneratePdfPayload,
    productCode?: string | null
  ): Promise<{
    success: boolean;
    data?: GeneratePdfResponse;
    error?: string;
  }> {
    return pickProductService(productCode).generatePdf(proposalId, payload);
  }

  async getProposalDocument(
    proposalId: string,
    productCode?: string | null
  ): Promise<{
    success: boolean;
    data?: GetProposalDocumentResponse;
    error?: string;
  }> {
    return pickProductService(productCode).getProposalDocument(proposalId);
  }

  async getInspectionDocument(
    policyId: string,
    type: 'refusal_letter' | 'improvement_letter',
  ): Promise<{
    success: boolean;
    data?: { key: string; url: string };
    error?: string;
  }> {
    return homeownerProposalsService.getInspectionDocument(policyId, type);
  }

  async getImprovementLetter(policyId: string): Promise<{
    success: boolean;
    data?: {
      text: string | null;
      attachments: { key: string; url: string }[];
    };
    error?: string;
  }> {
    return homeownerProposalsService.getImprovementLetter(policyId);
  }

  async sendEmail(payload: SendProposalEmailPayload): Promise<{
    success: boolean;
    error?: string;
    message?: string;
  }> {
    return commonProposalsService.sendEmail(payload);
  }

  async transmitProposal(
    policyId: string,
    productCode?: string | null
  ) {
    return pickProductService(productCode).transmitProposal(policyId);
  }
}

export const proposalsService = new ProposalsService();
